import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  completePurchases,
  createExtensionPurchase,
  deleteExtensionPurchase,
  getPurchaseSuggestions,
  listExtensionPurchases,
  updateExtensionPurchase,
} from '../../app/api';
import { useTranslation } from '../../app/LanguageContext';
import { applyCurrencyMask, formatCurrency, formatNumberAsCurrencyMask, parseCurrency } from '../../app/currency';
import { displayToIsoYmd, isoYmdToDisplay } from '../../app/date';
import CalendarDateTextField from '../../modules/core/public/CalendarDateTextField';
import { categoryLabel, formatQuantity, normalizeName, parseQuantity, todayYmd } from './pendingPurchasesUtils';

const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
const PRIORITIES = ['LOW', 'NORMAL', 'URGENT'];

function priorityChip(priority, translate) {
  if (priority === 'URGENT') {
    return <Chip size="small" color="error" label={translate('pending_purchases.priority_URGENT')} />;
  }
  if (priority === 'LOW') {
    return <Chip size="small" variant="outlined" label={translate('pending_purchases.priority_LOW')} />;
  }
  return null;
}

/** Full update payload for a pending purchase (PUT /purchases/{id} replaces every field). */
function pendingUpdatePayload(p, patch) {
  return {
    description: p.description,
    category: p.category,
    quantity: p.quantity,
    unit_cost: p.unit_cost,
    total_cost: 0,
    purchased_at: p.purchased_on,
    supplier_name: p.supplier_name,
    notes: p.notes,
    sku_id: p.sku_id || '',
    affects_stock: !!p.affects_stock,
    priority: p.priority,
    ...patch,
  };
}

function QuickAdd({ suggestions, disabled, onAdded, onError }) {
  const { translate } = useTranslation();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const typed = inputValue.trim();
    if (!typed || saving) return;
    const qty = parseQuantity(quantity);
    if (!(qty > 0)) {
      onError(translate('pending_purchases.error_quantity'));
      return;
    }
    const match =
      value && typeof value === 'object' && normalizeName(value.description) === normalizeName(typed)
        ? value
        : suggestions.find((s) => normalizeName(s.description) === normalizeName(typed));
    setSaving(true);
    try {
      const res = await createExtensionPurchase({
        status: 'PENDING',
        description: match ? match.description : typed,
        quantity: qty,
        category: match?.category || '',
        supplier_name: match?.supplier_name || '',
        sku_id: match?.sku_id || '',
        affects_stock: !!match?.affects_stock,
      });
      setValue(null);
      setInputValue('');
      setQuantity('1');
      onAdded(res?.data);
    } catch (e) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
      >
        <Autocomplete
          freeSolo
          disabled={disabled}
          sx={{ flex: 1, minWidth: 0 }}
          options={suggestions}
          value={value}
          inputValue={inputValue}
          onChange={(_, v) => setValue(v)}
          onInputChange={(_, v) => setInputValue(v)}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.description)}
          filterOptions={(opts, state) => {
            const q = normalizeName(state.inputValue);
            if (!q) return opts.slice(0, 20);
            return opts.filter((o) => normalizeName(o.description).includes(q)).slice(0, 20);
          }}
          renderOption={(props, o) => (
            <li {...props} key={o.description}>
              <Box>
                <Typography variant="body2">{o.description}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {translate('pending_purchases.last_price', { price: formatCurrency(o.unit_cost) })}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={translate('pending_purchases.quick_add_placeholder')} />
          )}
        />
        <TextField
          size="small"
          sx={{ width: 72 }}
          value={quantity}
          disabled={disabled}
          onChange={(e) => setQuantity(e.target.value)}
          inputProps={{ inputMode: 'decimal', 'aria-label': translate('pending_purchases.field_quantity') }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={disabled || !inputValue.trim() || saving}
          sx={{ minWidth: 0, px: 1.5 }}
          aria-label={translate('common.add')}
        >
          {saving ? <CircularProgress size={20} /> : <AddIcon />}
        </Button>
      </Box>
    </Paper>
  );
}

function EditPendingDialog({ purchase, categories, onClose, onSaved, onError }) {
  const { translate } = useTranslation();
  const [form, setForm] = useState({ description: '', quantity: '', priority: 'NORMAL', category: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (purchase) {
      setForm({
        description: purchase.description,
        quantity: formatQuantity(purchase.quantity),
        priority: purchase.priority || 'NORMAL',
        category: purchase.category || '',
        notes: purchase.notes || '',
      });
    }
  }, [purchase]);

  const save = async () => {
    const qty = parseQuantity(form.quantity);
    if (!(qty > 0)) {
      onError(translate('pending_purchases.error_quantity'));
      return;
    }
    if (!form.description.trim()) {
      onError(translate('pending_purchases.error_description'));
      return;
    }
    setSaving(true);
    try {
      await updateExtensionPurchase(
        purchase.id,
        pendingUpdatePayload(purchase, {
          description: form.description.trim(),
          quantity: qty,
          priority: form.priority,
          category: form.category,
          notes: form.notes,
        })
      );
      onSaved();
    } catch (e) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!purchase} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{translate('pending_purchases.edit_title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField
          label={translate('pending_purchases.field_description')}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
        <TextField
          label={translate('pending_purchases.field_quantity')}
          value={form.quantity}
          onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
          inputProps={{ inputMode: 'decimal' }}
        />
        <TextField
          select
          label={translate('pending_purchases.field_category')}
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
        >
          {categories.map((c) => (
            <MenuItem key={c.code} value={c.code}>
              {c.label || c.code}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={translate('pending_purchases.field_priority')}
          value={form.priority}
          onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
        >
          {PRIORITIES.map((p) => (
            <MenuItem key={p} value={p}>
              {translate(`pending_purchases.priority_${p}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={translate('pending_purchases.field_notes')}
          value={form.notes}
          multiline
          minRows={2}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('common.cancel')}</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {translate('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CompleteDialog({ open, purchases, categories, lastPrice, onClose, onDone, onError }) {
  const { translate } = useTranslation();
  const [lines, setLines] = useState([]);
  const [supplier, setSupplier] = useState('');
  const [date, setDate] = useState(todayYmd());
  const [dateDisplay, setDateDisplay] = useState(isoYmdToDisplay(todayYmd()));
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLines(
      purchases.map((p) => {
        const price = p.unit_cost > 0 ? p.unit_cost : lastPrice(p.description);
        return {
          purchase: p,
          quantity: formatQuantity(p.quantity),
          unitCost: price != null ? formatNumberAsCurrencyMask(price) : '',
          category: p.category || categories[0]?.code || '',
          affectsStock: !!p.sku_id && p.affects_stock,
        };
      })
    );
    const suppliers = [...new Set(purchases.map((p) => p.supplier_name).filter(Boolean))];
    setSupplier(suppliers.length === 1 ? suppliers[0] : '');
    setDate(todayYmd());
    setDateDisplay(isoYmdToDisplay(todayYmd()));
    setReceipt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setLine = (idx, patch) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const total = lines.reduce((sum, l) => {
    const q = parseQuantity(l.quantity);
    const uc = parseFloat(parseCurrency(l.unitCost));
    return sum + (q > 0 && uc >= 0 ? q * uc : 0);
  }, 0);

  const submit = async () => {
    const payloadLines = [];
    for (const l of lines) {
      const q = parseQuantity(l.quantity);
      if (!(q > 0)) {
        onError(translate('pending_purchases.error_quantity'));
        return;
      }
      if (!l.category) {
        onError(translate('pending_purchases.error_category', { name: l.purchase.description }));
        return;
      }
      const uc = parseFloat(parseCurrency(l.unitCost));
      payloadLines.push({
        purchase_id: l.purchase.id,
        quantity: q,
        unit_cost: Number.isFinite(uc) ? uc : 0,
        category: l.category,
        affects_stock: !!l.purchase.sku_id && l.affectsStock,
      });
    }
    setSaving(true);
    try {
      await completePurchases({ supplier_name: supplier.trim(), purchased_at: date, lines: payloadLines }, receipt);
      onDone(payloadLines.length);
    } catch (e) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="md">
      <DialogTitle>{translate('pending_purchases.complete_title')}</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2 }}>
          <TextField
            label={translate('pending_purchases.field_supplier')}
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            helperText={translate('pending_purchases.supplier_hint')}
          />
          <CalendarDateTextField
            label={translate('pending_purchases.field_date')}
            value={dateDisplay}
            onValueChange={setDateDisplay}
            isoValue={date}
            onIsoChange={(ymd) => {
              setDate(ymd);
              setDateDisplay(isoYmdToDisplay(ymd));
            }}
            calendarOpenLabel={translate('pending_purchases.field_date')}
            onBlur={() => {
              const y = displayToIsoYmd(dateDisplay);
              if (y) setDate(y);
              else setDateDisplay(isoYmdToDisplay(date));
            }}
          />
        </Box>
        <Stack spacing={1.5}>
          {lines.map((l, idx) => (
            <Paper key={l.purchase.id} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {l.purchase.description}
              </Typography>
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr 1fr', sm: '110px 150px 1fr' } }}>
                <TextField
                  size="small"
                  label={translate('pending_purchases.field_quantity')}
                  value={l.quantity}
                  onChange={(e) => setLine(idx, { quantity: e.target.value })}
                  inputProps={{ inputMode: 'decimal' }}
                  helperText={
                    parseQuantity(l.quantity) < l.purchase.quantity
                      ? translate('pending_purchases.partial_hint', {
                          rest: formatQuantity(l.purchase.quantity - parseQuantity(l.quantity)),
                        })
                      : ' '
                  }
                />
                <TextField
                  size="small"
                  label={translate('pending_purchases.field_unit_cost')}
                  value={l.unitCost}
                  onChange={(e) => setLine(idx, { unitCost: applyCurrencyMask(e.target.value) })}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <TextField
                  size="small"
                  select
                  label={translate('pending_purchases.field_category')}
                  value={l.category}
                  onChange={(e) => setLine(idx, { category: e.target.value })}
                  sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label || c.code}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              {l.purchase.sku_id ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={l.affectsStock}
                      onChange={(e) => setLine(idx, { affectsStock: e.target.checked })}
                    />
                  }
                  label={translate('pending_purchases.affects_stock')}
                />
              ) : null}
            </Paper>
          ))}
        </Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} size="small">
            {receipt ? receipt.name : translate('pending_purchases.attach_receipt')}
            <input
              type="file"
              hidden
              accept="application/pdf,image/*"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                e.target.value = '';
                if (f && f.size > RECEIPT_MAX_BYTES) {
                  onError(translate('pending_purchases.error_receipt_size'));
                  return;
                }
                setReceipt(f);
              }}
            />
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            {translate('pending_purchases.total', { total: formatCurrency(total) })}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {translate('common.cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || lines.length === 0}>
          {translate('pending_purchases.complete_confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Pending purchases: things still to buy. Marking them as bought turns them into purchases made. */
export default function PendingPurchasesPanel({ categories, disabled, onCompleted, notify }) {
  const { translate } = useTranslation();
  const [purchases, setPurchases] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);

  const notifyError = useCallback((message) => notify(message, 'error'), [notify]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listExtensionPurchases({ status: 'PENDING' });
      const list = res?.data?.purchases ?? [];
      setPurchases(list);
      setSelected((prev) => new Set([...prev].filter((id) => list.some((p) => p.id === id))));
    } catch (e) {
      notifyError(e.message);
    } finally {
      setLoading(false);
    }
  }, [notifyError]);

  const loadSuggestions = useCallback(async () => {
    try {
      const res = await getPurchaseSuggestions();
      setSuggestions(res?.data?.suggestions ?? []);
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    load();
    loadSuggestions();
  }, [load, loadSuggestions]);

  const priceByDescription = useMemo(() => {
    const m = new Map();
    suggestions.forEach((s) => m.set(normalizeName(s.description), s.unit_cost));
    return m;
  }, [suggestions]);
  const lastPrice = useCallback((description) => priceByDescription.get(normalizeName(description)) ?? null, [priceByDescription]);

  const selectedPurchases = useMemo(() => purchases.filter((p) => selected.has(p.id)), [purchases, selected]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleAdded = (p) => {
    if (p?.merged) notify(translate('pending_purchases.merged', { name: p.description }));
    load();
  };

  const handleDelete = async (p) => {
    try {
      await deleteExtensionPurchase(p.id);
      load();
    } catch (e) {
      notifyError(e.message);
    }
  };

  return (
    <Box sx={{ pb: selected.size > 0 ? 10 : 0 }}>
      <QuickAdd suggestions={suggestions} disabled={disabled} onAdded={handleAdded} onError={notifyError} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : purchases.length === 0 ? (
        <Alert severity="info">{translate('pending_purchases.empty')}</Alert>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {purchases.map((p, idx) => {
              const price = lastPrice(p.description);
              return (
                <ListItem
                  key={p.id}
                  divider={idx < purchases.length - 1}
                  sx={{ alignItems: 'flex-start', gap: 1, py: 1.25 }}
                  secondaryAction={
                    <Box>
                      <Tooltip title={translate('common.edit')}>
                        <IconButton size="small" onClick={() => setEditing(p)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={translate('pending_purchases.remove')}>
                        <IconButton size="small" onClick={() => handleDelete(p)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <Checkbox
                    edge="start"
                    checked={selected.has(p.id)}
                    onChange={() => toggle(p.id)}
                    sx={{ mt: -0.5 }}
                    inputProps={{ 'aria-label': p.description }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, pr: 9 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body1" fontWeight={500}>
                        {formatQuantity(p.quantity)} × {p.description}
                      </Typography>
                      {priorityChip(p.priority, translate)}
                    </Box>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {[
                        categoryLabel(categories, p.category),
                        p.supplier_name || null,
                        price != null ? translate('pending_purchases.last_price', { price: formatCurrency(price) }) : null,
                        p.requested_by_email || null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                    {p.notes ? (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {p.notes}
                      </Typography>
                    ) : null}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}

      {selected.size > 0 ? (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: 16,
            maxWidth: 1168,
            mx: 'auto',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: (t) => t.zIndex.appBar,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {translate('pending_purchases.selected_count', { count: selected.size })}
          </Typography>
          <Button variant="contained" onClick={() => setCompleteOpen(true)} disabled={disabled}>
            {translate('pending_purchases.complete_button')}
          </Button>
        </Paper>
      ) : null}

      <EditPendingDialog
        purchase={editing}
        categories={categories}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
        onError={notifyError}
      />
      <CompleteDialog
        open={completeOpen}
        purchases={selectedPurchases}
        categories={categories}
        lastPrice={lastPrice}
        onClose={() => setCompleteOpen(false)}
        onDone={(count) => {
          setCompleteOpen(false);
          setSelected(new Set());
          notify(translate('pending_purchases.complete_done', { count }));
          load();
          loadSuggestions();
          onCompleted();
        }}
        onError={notifyError}
      />
    </Box>
  );
}
